/* FocusPod delivery promise for the market configurator.
   Runs after market pricing and checkout setup, before the builder reads its data.
   Handles delivery messaging, stocked furniture choices and stock-arrival links.

   New Zealand and Australia (Sep 2026): the 3D studio is where a pod is made your own, and it checks out online
   (fp-configurator-checkout.js). In-stock pods are normally bought from the product and home pages (fp-nz-home-paths).
   - It opens on the in-stock pod (White exterior ST-03, Grey interior IN-02, the standard furniture or Pod only). Ordered before 2pm local
     time on a working day (NZ: Mon to Fri, not a national public holiday; AU: Mon to Fri Queensland time, not a Queensland public holiday)
     it is dispatched the same day.
   - Change a colour, add a custom finish, choose another furniture layout, privacy glass or air conditioning and the same pod becomes
     made to order (about four weeks). A clear note says so, with a one-tap "Back to in stock". Accessories never change the path.
   - Delivery: NZ is calculated at checkout; AU is free Australia-wide.
   window.FocusPodNzInStock exposes the in-stock rule (fp-configurator-checkout.js uses it).
   Swatches are found by [data-color-group][data-code]; the in-stock dot is keyed on a data attribute, never a class.
   Arriving links:
     ?model=<key>&add=stock[&furniture=standard]   put the pictured pod in the studio cart (fallback only)
     ?model=<key>&preview=stock[&furniture=...]    open on the in-stock pod
     ?model=<key>&design=1                         open ready to change, with the colour hint showing
     &exterior=ST-05 &interior=IN-04 &furniture=<none|standard|AddonN> &glass=<front|both> &aircon=yes   apply those choices on arrival
   Public holidays: 2026 and 2027 are listed below (same lists as fp-delivery-date); extend both for 2028. Other markets are unchanged. */
(() => {
  'use strict';
  const json = document.getElementById('fp-us-engine-source')?.content.querySelector('[data-builder-data]');
  if (!json) return;
  let data;
  try { data = JSON.parse(json.textContent); } catch { return; }
  if (!data || !data.copy || !data.currency) return;
  if(window.FocusPodQuoteFirst){window.FocusPodQuoteFirst.prepare(data);json.textContent=JSON.stringify(data);return;}
  const au = data.country === 'AU';
  const nz = au || data.country === 'NZ' || /(^|\.)focuspod\.co\.nz$/.test(location.hostname);
  const locale = data.country === 'US' ? 'en-US' : 'en-GB';
  const format = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long' });
  const at = days => {
    const day = new Date();
    day.setHours(12, 0, 0, 0);
    day.setDate(day.getDate() + days);
    if (day.getDay() === 6) day.setDate(day.getDate() + 2);
    else if (day.getDay() === 0) day.setDate(day.getDate() + 1);
    return format.format(day);
  };
  const date = at(28);
  const paid = au ? 'Delivery is free Australia-wide and GST is added at checkout.' : nz ? 'Delivery and any GST are calculated at checkout.' : 'Delivery is calculated at checkout.';
  const modular = ' Pods are modular and assembled on site, so they can be installed almost anywhere, from upper floors to tight fit-outs.';
  const checkout = !!data.checkoutVersion;
  const cutoff = au ? '2pm AEST' : '2pm NZ time';
  Object.assign(data.copy, {
    destination_note: data.currency + ' prices ' + (data.taxNote || '') + '. ' + paid,
    preview_checkout: nz
      ? 'In-stock pods (White with Grey interior) ordered before ' + cutoff + ' on a working day are dispatched the same day. Made-to-order pods are delivered by ' + date + '.' + modular
      : 'Order today, delivered by ' + date + '.' + modular,
    lead_time: nz
      ? 'In stock (White with Grey interior): same-day dispatch when ordered before ' + cutoff + ' on a working day \u00b7 Made to order: delivered by ' + date
      : 'Order today, delivered by ' + date + '.'
  });
  json.textContent = JSON.stringify(data);
  window.FocusPodDeliveryDate = date;
  const note = 'Order today, delivered by ' + date + '.';
  document.querySelectorAll('.us-configurator .us-purchase-note').forEach(n => { n.textContent = note; });
  if (!nz) return;

  /* Same-day dispatch: before 2pm local time on a working day. */
  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const TZ = au ? 'Australia/Brisbane' : 'Pacific/Auckland';
  const HOLIDAYS = au
    ? ['2026-01-01', '2026-01-26', '2026-04-03', '2026-04-06', '2026-05-04', '2026-10-05', '2026-12-25', '2026-12-28',
      '2027-01-01', '2027-01-26', '2027-03-26', '2027-03-29', '2027-04-26', '2027-05-03', '2027-10-04', '2027-12-27', '2027-12-28']
    : ['2026-01-01', '2026-01-02', '2026-02-06', '2026-04-03', '2026-04-06', '2026-04-27', '2026-06-01', '2026-07-10', '2026-10-26', '2026-12-25', '2026-12-28',
      '2027-01-01', '2027-01-04', '2027-02-08', '2027-03-26', '2027-03-29', '2027-04-26', '2027-06-07', '2027-06-25', '2027-10-25', '2027-12-27', '2027-12-28'];
  const dispatch = () => {
    const o = {};
    const fallback = { today: false, short: 'Same-day dispatch', long: 'order before ' + cutoff + ' for same-day dispatch', sentence: 'Order before ' + cutoff + ' for same-day dispatch' };
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' })
        .formatToParts(new Date()).forEach(x => { o[x.type] = x.value; });
    } catch { return fallback; }
    const mins = (parseInt(o.hour, 10) % 24) * 60 + parseInt(o.minute, 10);
    const base = Date.UTC(+o.year, +o.month - 1, +o.day);
    if (isNaN(mins) || isNaN(base)) return fallback;
    const work = t => { const w = new Date(t).getUTCDay(); return w >= 1 && w <= 5 && !HOLIDAYS.includes(new Date(t).toISOString().slice(0, 10)); };
    if (work(base) && mins < 840) {
      const left = 840 - mins, h = Math.floor(left / 60), m = left % 60, t = (h ? h + ' hr ' : '') + m + ' min';
      return { today: true, short: 'Dispatched today', long: 'order within ' + t + ' for same-day dispatch', sentence: 'Order within ' + t + ' for same-day dispatch' };
    }
    let n = base, i = 0;
    do { n += 864e5; i++; } while (!work(n) && i < 14);
    const w = i === 1 ? 'tomorrow' : DAYS[new Date(n).getUTCDay()], when = w === 'tomorrow' ? w : 'on ' + w;
    return { today: false, short: 'Dispatched ' + w, long: 'order now for dispatch ' + when, sentence: 'Order now for dispatch ' + when };
  };
  if (!window.FocusPodDispatch) window.FocusPodDispatch = dispatch;

  const stockFurniture = model => au ? ({duo:'Addon3',team:'Addon2'}[model] || 'standard') : 'standard';
  const stockFurnitureLabel = model => au && model === 'duo' ? 'Benches & table' : au && model === 'team' ? 'Lounge + rear centre wall' : ((data.models||[]).find(m=>m.key===model)?.standardFurnitureLabel || data.copy.standard_furniture);
  const FACTORY = ['privacy_glass_front', 'privacy_glass_both', 'air_conditioning'];
  const reason = s => {
    if (!s) return 'colour';
    if (s.customExterior || s.customInterior || s.exterior !== 'ST-03' || s.interior !== 'IN-02') return 'colour';
    if ((s.extras || []).some(code => FACTORY.includes(code))) return 'factory';
    if (s.furniture && s.furniture !== 'none' && s.furniture !== stockFurniture(s.model)) return 'furniture';
    return '';
  };
  const inStock = s => !reason(s);
  window.FocusPodNzInStock = inStock;
  const SW = (group, code) => '[data-color-group="' + group + '"][data-code="' + code + '"]';

  const params = new URLSearchParams(location.search);
  const safe = v => /^[A-Za-z0-9_-]{1,20}$/.test(v || '') ? v : '';
  const arrive = {
    add: params.get('add') === 'stock',
    preview: params.get('preview') === 'stock',
    design: params.get('design') === '1',
    furniture: safe(params.get('furniture')),
    installation: params.get('installation') === 'yes',
    hasInstallation: ['yes', 'no'].includes(params.get('installation')),
    hasQuantity: params.has('quantity'),
    quantity: Math.max(1, Math.min(20, parseInt(params.get('quantity'), 10) || 1)),
    exterior: safe(params.get('exterior')),
    interior: safe(params.get('interior')),
    glass: ['front', 'both'].includes(params.get('glass')) ? params.get('glass') : '',
    aircon: params.get('aircon') === 'yes'
  };
  let added = null;
  if (arrive.add) {
    const model = (data.models || []).find(m => m.key === params.get('model'));
    if (model) {
      const furniture = arrive.furniture && arrive.furniture !== 'none' ? stockFurniture(model.key) : 'none';
      const included = (data.extras || []).filter(e => e.included).map(e => e.code);
      const key = 'focuspod:regional:' + data.country + ':' + data.version;
      try {
        let saved = null;
        try { saved = JSON.parse(localStorage.getItem(key) || 'null'); } catch { saved = null; }
        const designs = saved && saved.country === data.country && saved.version === data.version && Array.isArray(saved.designs) ? saved.designs : [];
        const pods = designs.reduce((sum, d) => sum + (Number(d.quantity) || 0), 0);
        const plain = d => d.model === model.key && d.furniture === furniture && inStock(d) && !!d.installation === arrive.installation
          && (d.extras || []).every(code => included.includes(code));
        const same = designs.find(plain);
        if (pods + arrive.quantity <= 100 && (same || designs.length < 20)) {
          if (same) same.quantity = (Number(same.quantity) || 1) + arrive.quantity;
          else if (designs.length < 20) designs.push({ model: model.key, quantity: arrive.quantity, exterior: 'ST-03', interior: 'IN-02', furniture, extras: included, customExterior: '', customInterior: '', installation: arrive.installation });
          localStorage.setItem(key, JSON.stringify({ version: data.version, country: data.country, designs }));
          added = { name: model.name, furniture, quantity: same ? same.quantity : arrive.quantity };
        } else {
          added = { full: true };
        }
      } catch { added = { storage: true }; }
    }
  }
  ['add', 'preview', 'design', 'furniture', 'installation', 'quantity', 'exterior', 'interior', 'glass', 'aircon', 'from'].forEach(k => params.delete(k));
  const query = params.toString();
  try { history.replaceState(null, '', location.pathname + (query ? '?' + query : '') + location.hash); } catch {}

  const css = '.fp-nz-path{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin:18px 0 0}'
    + '.fp-nz-path button{display:flex;flex-direction:column;align-items:flex-start;gap:2px;min-width:0;padding:11px 14px;border:1px solid #e3e9f1;border-radius:12px;background:#fff;color:#0a0f1a;text-align:left;font:13px/1.4 Inter,sans-serif;cursor:pointer;transition:border-color .18s,box-shadow .18s,background .18s}'
    + '.fp-nz-path button:hover{border-color:#bacee7}'
    + '.fp-nz-path button[aria-pressed="true"]{border-color:#87bfff;box-shadow:0 0 0 1px #87bfff;background:#f8fafd}'
    + '.fp-nz-path button:focus-visible{outline:2px solid #3a7bd5;outline-offset:2px}'
    + '.fp-nz-path-tag{font:400 9px/1.6 "Martian Mono",monospace;letter-spacing:.12em;text-transform:uppercase;color:#3a7bd5}'
    + '.fp-nz-path strong{font:600 13.5px/1.35 "Schibsted Grotesk",Inter,sans-serif;letter-spacing:-.02em}'
    + '.fp-nz-path small{color:#5d6879;font-size:11px;line-height:1.4}'
    + '.fp-nz-path strong,.fp-nz-path small{max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}'
    + '.fp-nz-path-note{display:flex;flex-wrap:wrap;align-items:center;gap:6px 12px;margin:10px 0 18px;font:12px/1.55 Inter,sans-serif;color:#5d6879}'
    + '.fp-nz-path-note.is-custom{padding:10px 12px;border:1px solid #87bfff;border-radius:10px;background:#f8fafd;color:#0a0f1a}'
    + '.fp-nz-path-note>span{flex:1 1 240px}'
    + '.fp-nz-undo{flex:0 0 auto;padding:6px 12px;border:1px solid #0a0f1a;border-radius:999px;background:#fff;color:#0a0f1a;font:600 11px/1.3 Inter,sans-serif;cursor:pointer}'
    + '.fp-nz-undo:hover{background:#0a0f1a;color:#fff}'
    + '[data-fp-nz-instock]{position:relative;overflow:visible!important}'
    + '[data-fp-nz-instock]::after{content:"";position:absolute;right:-1px;top:-1px;width:9px;height:9px;border-radius:50%;background:#3a7bd5;box-shadow:0 0 0 2px #fff;pointer-events:none}'
    + '.fp-nz-legend{display:flex;align-items:center;gap:8px;margin:12px 0 0;padding:0 4px;font:500 12px/1.5 Inter,sans-serif;color:#0a0f1a}'
    + '.fp-nz-legend:before{content:"";flex:0 0 7px;width:7px;height:7px;border-radius:50%;background:#3a7bd5}'
    + '.fp-nz-legend span{font-weight:400;color:#5d6879}'
    + '.fp-nz-pulse{border-radius:12px;animation:fpNzPulse 1.6s ease-out 1}'
    + '@keyframes fpNzPulse{0%{box-shadow:0 0 0 0 rgb(58 123 213 / .45)}100%{box-shadow:0 0 0 14px rgb(58 123 213 / 0)}}'
    + '.fp-nz-cart-path{display:flex;align-items:center;gap:8px;margin:4px 0 8px;font:500 12px/1.4 Inter,sans-serif;color:#0a0f1a}'
    + '.fp-nz-cart-path:before{content:"";flex:0 0 6px;width:6px;height:6px;border-radius:50%;background:#3a7bd5;box-shadow:0 0 0 3px rgb(58 123 213 / .12)}'
    + '.fp-nz-cart-path[data-path="custom"]:before{background:#a7b9d0;box-shadow:none}'
    + '.fp-nz-cart-summary{margin:0 0 12px;padding:12px 14px;border:1px solid #e3e9f1;border-radius:10px;background:#f8fafd;font:12px/1.6 Inter,sans-serif;color:#0a0f1a}'
    + '.fp-nz-added{margin:0 0 12px;padding:13px 15px;border:1px solid #87bfff;border-radius:10px;background:#f8fafd;color:#5d6879;font:12px/1.6 Inter,sans-serif}'
    + '.fp-nz-added strong{display:block;margin-bottom:2px;font:600 14px/1.4 "Schibsted Grotesk",Inter,sans-serif;color:#0a0f1a}'
    + '.fp-nz-added.is-error{border-color:#f0c2bd;background:#fff6f5;color:#7a1712}'
    + '.fp-nz-added-actions{display:flex;gap:8px;margin-top:10px}'
    + '.fp-nz-added-actions button{flex:1;padding:10px 12px;border-radius:999px;font:600 12px/1.2 Inter,sans-serif;cursor:pointer}'
    + '.fp-nz-added-go{border:1px solid #0a0f1a;background:#0a0f1a;color:#fff}'
    + '.fp-nz-added-keep{border:1px solid #e3e9f1;background:#fff;color:#0a0f1a}'
    + '.us-volume-rules,#us-volume-open{display:none!important}'
    + '@media(max-width:768px){.fp-nz-path{position:sticky;top:0;z-index:6;gap:6px;margin:10px 0 0;padding:6px 0;background:#fff}'
    + '.fp-nz-path button{flex-direction:row;align-items:center;justify-content:center;padding:9px 10px;border-radius:999px}'
    + '.fp-nz-path strong,.fp-nz-path small{display:none}'
    + '.fp-nz-path-tag{font-size:10px;letter-spacing:.08em}'
    + '.fp-nz-path button[aria-pressed="true"] .fp-nz-path-tag{color:#0a0f1a}'
    + '.fp-nz-path-note{margin:6px 0 14px;font-size:11px}}';

  const start = () => {
    const root = document.querySelector('.us-configurator');
    const api = window.FocusPodRegionalBuilder;
    if (!root || !api || typeof api.snapshot !== 'function') return false;
    if (root.dataset.fpNzStock) return true;
    root.dataset.fpNzStock = 'true';
    const style = document.createElement('style');
    style.textContent = css;
    document.head.append(style);
    const q = s => root.querySelector(s);
    const click = s => { const b = q(s); if (b) b.click(); };
    const el = (tag, text, cls) => { const n = document.createElement(tag); if (text) n.textContent = text; if (cls) n.className = cls; return n; };
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let customHint = false;

    const availabilityCopy = au ? (() => {
      const defaults = {context:'Your current design',stock_title:'In stock',custom_title:'Made to order',stock_detail:'White exterior · Grey interior',custom_timing:'About 4 weeks',estimate:'Estimated delivery · [date]',sooner:'Need it sooner?',intro:'For earlier dispatch, change:',apply:'Use in-stock setup',keep:'Keep my design',undo:'Undo changes',exterior:'Exterior',interior:'Interior',furniture:'Furniture',privacy:'Privacy glass',cooling:'Air conditioning',clear:'Clear glass',standard_air:'Standard ventilation',stock_applied:'In-stock setup applied',colour_reason:'Your colours are made to order.',furniture_reason:'Your furniture is made to order.',factory_reason:'Your upgrades are fitted to order.',combined_reason:'Your choices need a custom build.',all_colours:'Choose any colour.',dispatch_estimate:'[dispatch] if ordered now'};
      try { const supplied=JSON.parse(document.getElementById('fp-availability-copy')?.textContent||'{}');Object.keys(defaults).forEach(key=>{if(typeof supplied[key]==='string'&&supplied[key].trim())defaults[key]=decodeURIComponent(supplied[key].replace(/\+/g,' '));}); } catch {}
      defaults.custom_title='Pre-order';
      return defaults;
    })() : null;
    const compactDate = au ? new Intl.DateTimeFormat(locale,{day:'numeric',month:'short'}).format((()=>{const day=new Date();day.setHours(12,0,0,0);day.setDate(day.getDate()+28);if(day.getDay()===6)day.setDate(day.getDate()+2);else if(day.getDay()===0)day.setDate(day.getDate()+1);return day;})()) : date;
    const setText = (node,value) => { if(node.textContent!==String(value))node.textContent=String(value); };
    const stateKey = state => JSON.stringify({...state,extras:[...(state.extras||[])].sort()});
    const box = el('div', '', au ? 'fp-nz-path fp-au-availability' : 'fp-nz-path');
    box.setAttribute('role', 'group');
    box.setAttribute('aria-label', au ? availabilityCopy.context : 'In stock or made to order');
    const card = (path, tag, title) => {
      const b = el('button');b.type = 'button';b.dataset.fpPath = path;
      const when = el('small');b.append(el('span', tag, 'fp-nz-path-tag'), el('strong', title), when);box.append(b);return {b,when};
    };
    const stock = au ? null : card('stock','In stock','White with Grey interior');
    const custom = au ? null : card('custom','Made to order','Any colour, layout or upgrade');
    if(custom)custom.when.textContent='Delivered by '+date;
    const stockBtn = au ? el('button',availabilityCopy.apply,'fp-au-availability-apply') : stock.b, customBtn = custom?.b;
    stockBtn.type='button';
    const pathNote=el('div','','fp-nz-path-note'),noteText=el('span'),undo=el('button',au?availabilityCopy.undo:'Back to in stock \u21ba',au?'fp-au-availability-undo':'fp-nz-undo');
    undo.type='button';undo.hidden=true;
    let availability=null,undoSelection=null,updatingSelection=false;
    if(au){
      pathNote.hidden=true;
      const heading=el('p',availabilityCopy.context,'fp-au-availability-heading'),status=el('div','','fp-au-availability-status'),title=el('strong','','fp-au-availability-title'),timing=el('span','','fp-au-availability-timing'),detail=el('p','','fp-au-availability-detail'),options=el('details','','fp-au-availability-options'),summary=el('summary',availabilityCopy.sooner,'fp-au-availability-summary'),changes=el('ul','','fp-au-availability-changes'),keep=el('button',availabilityCopy.keep,'fp-au-availability-keep'),live=el('span','','fp-au-availability-live');
      keep.type='button';live.setAttribute('aria-live','polite');live.setAttribute('aria-atomic','true');
      status.append(title,timing);options.append(summary,el('p',availabilityCopy.intro),changes,stockBtn,keep);box.append(heading,status,detail,options,undo,live);
      availability={title,timing,detail,options,summary,changes,keep,live,changeKey:null,announcementKey:null};
      keep.addEventListener('click',()=>{options.open=false;summary.focus({preventScroll:true});});
    }else{pathNote.setAttribute('aria-live','polite');pathNote.append(noteText,undo);}
    const header=q('.config-header');
    if(header)header.after(box,pathNote);else{const choices=q('.us-choice-header');if(choices)choices.before(box,pathNote);}

    const colourName=(state,group)=>state[group==='exterior'?'customExterior':'customInterior']||(data[group]||[]).find(colour=>colour.code===state[group])?.name||state[group];
    const stockChanges=state=>{
      const changes=[],model=(data.models||[]).find(item=>item.key===state.model),extras=state.extras||[];
      ['exterior','interior'].forEach((group,index)=>{const code=index?'IN-02':'ST-03';if(state[group]!==code||state[index?'customInterior':'customExterior'])changes.push({key:group,text:availabilityCopy[group]+': '+colourName(state,group)+' → '+((data[group]||[]).find(colour=>colour.code===code)?.name||code)});});
      if(state.furniture&&state.furniture!=='none'&&state.furniture!==stockFurniture(state.model))changes.push({key:'furniture',text:availabilityCopy.furniture+': '+((data.layouts?.[state.model]||[]).find(layout=>layout.key===state.furniture)?.label||state.furniture)+' → '+stockFurnitureLabel(state.model)});
      const privacy=extras.filter(code=>code.indexOf('privacy_glass')===0);if(privacy.length)changes.push({key:'privacy',text:availabilityCopy.privacy+': '+privacy.map(code=>(data.extras||[]).find(extra=>extra.code===code)?.label||code).join(' + ')+' → '+availabilityCopy.clear});
      if(extras.includes('air_conditioning'))changes.push({key:'cooling',text:availabilityCopy.cooling+': '+((data.extras||[]).find(extra=>extra.code==='air_conditioning')?.label||availabilityCopy.cooling)+' → '+availabilityCopy.standard_air});
      return changes;
    };
    function updateAvailability(state,x){
      const ok=inStock(state),changes=stockChanges(state),groups=new Set(changes.map(change=>['exterior','interior'].includes(change.key)?'colour':['privacy','cooling'].includes(change.key)?'factory':'furniture'));
      const explanation=ok?availabilityCopy.stock_detail:groups.size>1?availabilityCopy.combined_reason:availabilityCopy[(groups.values().next().value||reason(state))+'_reason'];
      const title=ok?availabilityCopy.stock_title:availabilityCopy.custom_title;
      if(box.dataset.state!==(ok?'stock':'custom'))box.dataset.state=ok?'stock':'custom';
      setText(availability.title,title);setText(availability.timing,ok?availabilityCopy.dispatch_estimate.replace('[dispatch]',x.short):availabilityCopy.estimate.replace('[date]',compactDate));setText(availability.detail,explanation);
      if(availability.options.hidden!==ok)availability.options.hidden=ok;if(ok&&availability.options.open)availability.options.open=false;
      if(stockBtn.disabled!==!changes.length)stockBtn.disabled=!changes.length;
      const changeKey=JSON.stringify(changes);if(changeKey!==availability.changeKey){availability.changes.replaceChildren(...changes.map(change=>el('li',change.text)));availability.changeKey=changeKey;}
      if(undoSelection&&!updatingSelection&&stateKey(state)!==undoSelection.after)undoSelection=null;
      const hidden=!undoSelection;if(undo.hidden!==hidden)undo.hidden=hidden;
      const announcementKey=(ok?'stock':'custom')+'|'+explanation;
      if(availability.announcementKey!==announcementKey){if(availability.announcementKey!==null)setText(availability.live,title+'. '+explanation);availability.announcementKey=announcementKey;}
    }
    function setSelectionExtras(wanted){
      const inputs=[...root.querySelectorAll('[data-extra]')];
      [false,true].forEach(checked=>inputs.forEach(input=>{if(input.disabled||wanted.includes(input.dataset.extra)!==checked||input.checked===checked)return;input.checked=checked;input.dispatchEvent(new Event('change',{bubbles:true}));}));
    }
    function restoreSelection(saved){
      const radio=q('#builder-furniture input[name="furniture"][value="'+saved.furniture+'"]');if(radio&&!radio.checked)radio.click();
      ['exterior','interior'].forEach(group=>{click(SW(group,saved[group]));const input=q('#builder-custom-'+group),value=saved[group==='exterior'?'customExterior':'customInterior']||'';if(input&&input.value!==value){input.value=value;input.dispatchEvent(new Event('input',{bubbles:true}));}});
      setSelectionExtras(saved.extras||[]);
    }

    root.querySelectorAll(SW('exterior', 'ST-03') + ',' + SW('interior', 'IN-02')).forEach(b => {
      b.setAttribute('data-fp-nz-instock', '');
      b.title = (b.title || '') + ' \u00b7 in stock';
      b.setAttribute('aria-label', (b.getAttribute('aria-label') || '') + ', in stock');
    });
    const legends = [];
    root.querySelectorAll('[data-finish-palette]').forEach(p => {
      const ext = p.dataset.finishPalette === 'exterior';
      const legend = el('p', '', 'fp-nz-legend');
      if(au)legend.append(document.createTextNode(availabilityCopy.all_colours));else legend.append(document.createTextNode(ext ? 'White is in stock' : 'Grey is in stock'), el('span', ext ? '\u00b7 any other colour is made to order' : '\u00b7 any other tone is made to order'));
      p.after(legend);
      legends.push([p, legend]);
    });

    const applyStock = () => {
      customHint = false;
      root.querySelectorAll('[data-custom-close]').forEach(b => { const panel = b.closest('[data-custom-panel]'); if (panel && !panel.hidden) b.click(); });
      click(SW('exterior', 'ST-03'));
      click(SW('interior', 'IN-02'));
      let s;
      try { s = api.snapshot().state; } catch { s = null; }
      if (s && s.extras.some(code => code.indexOf('privacy_glass') === 0)) click('[data-us-glass="none"]');
      if (s && s.extras.includes('air_conditioning')) click('[data-us-aircon="no"]');
      if (s && s.furniture && s.furniture !== 'none' && s.furniture !== stockFurniture(s.model)) {
        const radio = root.querySelector('#builder-furniture input[name="furniture"][value="' + stockFurniture(s.model) + '"]');
        if (radio && !radio.checked) radio.click();
      }
      setTimeout(update, 60);
    };
    stockBtn.addEventListener('click',()=>{
      if(!au){applyStock();return;}
      const before=api.snapshot().state;if(inStock(before))return;
      updatingSelection=true;applyStock();setSelectionExtras((before.extras||[]).filter(code=>!FACTORY.includes(code)));const after=api.snapshot().state;
      undoSelection={before,after:stateKey(after)};updatingSelection=false;update();
      if(inStock(after))setText(availability.live,availabilityCopy.stock_applied);if(!undo.hidden)undo.focus({preventScroll:true});
    });
    undo.addEventListener('click',()=>{
      if(!au){stockBtn.click();return;}
      if(!undoSelection||stateKey(api.snapshot().state)!==undoSelection.after){undoSelection=null;update();return;}
      const saved=undoSelection.before;updatingSelection=true;undoSelection=null;restoreSelection(saved);updatingSelection=false;update();availability.summary.focus({preventScroll:true});
    });
    if(customBtn)customBtn.addEventListener('click', () => {
      let s;
      try { s = api.snapshot().state; } catch { s = null; }
      customHint = inStock(s);
      click('[data-us-step-tab="exterior"]');
      setTimeout(() => {
        const palette = q('[data-finish-palette="exterior"]');
        if (!palette || palette.hidden) return;
        palette.scrollIntoView({ block: 'center', behavior: smooth ? 'smooth' : 'instant' });
        palette.classList.remove('fp-nz-pulse');
        void palette.offsetWidth;
        palette.classList.add('fp-nz-pulse');
        setTimeout(() => palette.classList.remove('fp-nz-pulse'), 1700);
      }, 80);
      setTimeout(update, 60);
    });

    const cartSummary = el('p', '', 'fp-nz-cart-summary');
    cartSummary.hidden = true;
    const totalRow = q('#builder-cart .cart-total-row');
    if (totalRow) totalRow.before(cartSummary);

    function update() {
      let snap;
      try { snap = api.snapshot(); } catch { return; }
      const x = dispatch();
      const why = reason(snap.state), ok = !why;
      if (!ok) customHint = false;
      if(au){updateAvailability(snap.state,x);legends.forEach(([p,legend])=>{if(legend.hidden!==p.hidden)legend.hidden=p.hidden;});}
      else{
      stock.when.textContent = x.today ? 'Order by 2pm, dispatched today' : x.short + ' if ordered now';
      stockBtn.setAttribute('aria-pressed', String(ok));
      customBtn.setAttribute('aria-pressed', String(!ok));
      legends.forEach(([p, legend]) => { legend.hidden = p.hidden; });
      pathNote.classList.toggle('is-custom', !ok);
      undo.hidden = ok;
      noteText.textContent = ok
        ? (customHint
          ? 'Pick any colour below, or open Custom finish. Your pod then becomes made to order, delivered by ' + date + '.'
          : 'Accessories and Pod only keep it in stock. Another colour, furniture layout, privacy glass or air conditioning makes it made to order.')
        : 'Now made to order, delivered by ' + date + '. ' + (why === 'factory'
          ? 'Privacy glass and air conditioning are fitted when the pod is made.'
          : why === 'furniture'
            ? 'This furniture layout is made to order; the standard furniture and Pod only are in stock.'
            : 'Your colour choice is made to order; White with Grey interior is in stock.');
      }
      const purchase = ok ? 'In stock. ' + x.sentence + '.' : au ? 'Made to order · Estimated delivery ' + date + '.' : 'Made to order. Delivered by ' + date + '.';
      root.querySelectorAll('.us-purchase-note').forEach(n => { if(au)setText(n,purchase);else n.textContent=purchase; });
      const approval = q('#builder-approval-note');
      if(approval&&checkout){if(au)setText(approval,paid);else approval.textContent=paid;}

      root.querySelectorAll('#builder-cart-items .cart-item, #builder-cart-items .fpb-cart-item').forEach((item, i) => {
        const design = snap.designs[i];
        if (!design) return;
        let line = item.querySelector('.fp-nz-cart-path');
        if (!line) {
          line = el('p', '', 'fp-nz-cart-path');
          const anchor = item.querySelector('h3 + p') || item.querySelector('h3');
          if (anchor) anchor.after(line); else item.prepend(line);
        }
        const inst = inStock(design);
        const cartPath=inst?'stock':'custom',cartText=inst?'In stock \u00b7 '+x.short.toLowerCase():au?'Made to order \u00b7 Estimated delivery '+date:'Made to order \u00b7 delivered by '+date;
        if(au){if(line.dataset.path!==cartPath)line.dataset.path=cartPath;setText(line,cartText);}else{line.dataset.path=cartPath;line.textContent=cartText;}
      });
      const stockCount = snap.designs.filter(inStock).length, customCount = snap.designs.length - stockCount;
      if(!au||cartSummary.hidden!==!snap.designs.length)cartSummary.hidden = !snap.designs.length;
      const cartText = !customCount
        ? 'Everything in your cart is in stock. ' + x.sentence + '.'
        : !stockCount
          ? au ? 'Everything in your cart is made to order. Estimated delivery ' + date + '.' : 'Everything in your cart is made to order and delivered by ' + date + '.'
          : 'Your in-stock pods are ' + x.short.toLowerCase() + (au ? '. Estimated delivery for your made-to-order pods: ' : ' and your made-to-order pods are delivered by ') + date + '. Prefer one delivery? Tell us and we will bring everything together.';
      if(au)setText(cartSummary,cartText);else cartSummary.textContent=cartText;
      if (added && added.live) {if(au)setText(added.live,' '+x.sentence+'.');else added.live.textContent = ' ' + x.sentence + '.';}
    }

    const applyArrival = () => {
      if (arrive.exterior) click(SW('exterior', arrive.exterior));
      if (arrive.interior) click(SW('interior', arrive.interior));
      if (arrive.furniture && !arrive.add) {
        const radio = root.querySelector('#builder-furniture input[name="furniture"][value="' + arrive.furniture + '"]');
        if (radio && !radio.checked) radio.click();
      }
      if (au && arrive.design && !arrive.add) {
        const quantity = q('#builder-quantity');
        if (arrive.hasQuantity && quantity) {
          quantity.value = String(arrive.quantity);
          quantity.dispatchEvent(new Event('change', { bubbles: true }));
        }
        const installation = q('#builder-installation');
        if (arrive.hasInstallation && installation && installation.checked !== arrive.installation) {
          installation.checked = arrive.installation;
          installation.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      if (arrive.glass) click('[data-us-glass="' + arrive.glass + '"]');
      if (arrive.aircon) click('[data-us-aircon="yes"]');
      if (arrive.design) {
        let s;
        try { s = api.snapshot().state; } catch { s = null; }
        customHint = inStock(s);
        click(au ? '[data-us-step-tab="furniture"]' : '[data-us-step-tab="exterior"]');
      }
    };
    applyArrival();

    const items = q('#builder-cart-items');
    if (added && items) {
      const msg = el('div', '', 'fp-nz-added');
      msg.setAttribute('role', 'status');
      if (added.name) {
        const live = document.createTextNode('');
        const actions = el('div', '', 'fp-nz-added-actions');
        const go = el('button', 'Check out now', 'fp-nz-added-go');
        const keep = el('button', 'Keep designing', 'fp-nz-added-keep');
        go.type = keep.type = 'button';
        go.addEventListener('click', () => click('#builder-order-review'));
        keep.addEventListener('click', () => click('#us-cart-close'));
        actions.append(go, keep);
        msg.append(el('strong', 'Added to your cart'), document.createTextNode('FocusPod ' + added.name + ', in stock in White with Grey interior' + (added.furniture !== 'none' ? ', with the standard furniture' : ', pod only') + (added.quantity > 1 ? ' (now ' + added.quantity + ' in your cart)' : '') + '.'), live, actions);
        added.live = live;
      } else {
        msg.classList.add('is-error');
        msg.textContent = added.full ? 'Your cart is full. For a larger order, contact us and we will put it together for you.' : 'Your browser blocked the cart. Allow site data for this site, or contact us to order.';
      }
      items.before(msg);
      setTimeout(() => click('#us-cart-open'), 400);
    }

    update();
    setTimeout(update, 120);
    setInterval(update, 30000);
    ['click', 'change', 'input'].forEach(type => root.addEventListener(type, () => setTimeout(update, 60)));
    if (items && window.MutationObserver) {
      let queued = false;
      new MutationObserver(() => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => { queued = false; update(); });
      }).observe(items, { childList: true });
    }
    return true;
  };
  let tries = 0;
  const wait = () => { if (start() || ++tries > 150) return; setTimeout(wait, 100); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wait, { once: true });
  else wait();
})();



